import React, { useEffect, useState } from "react";
import { Prompt } from "react-router-dom";
import { TextField, Switch, Box, Grid, Backdrop, Button } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import { useFormik } from "formik";
import { number, object, string } from "yup";
import { useAppDispatch } from "src/app-redux/hooks";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { IVehicleType } from "src/common/typings/vehicle";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonButton from "../../../../../../components/CommonButton/CommonButton";
import { columns } from "./columns";
import { setisDirtyValue } from "src/app-redux/configuration/configurationSlice";
import { IAddBannedItem } from "src/common/typings/configuration";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import { Check, Close } from "@material-ui/icons";
import { Tooltip } from "@mui/material";
import { getAllCargoUnits } from "src/app-redux/warehouse/actions/wareHouseActions";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonLoader from "src/components/Loader/CommonLoader";

const useStyles = makeStyles((theme: Theme) => ({
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
    paddingRight: 0,
  },
  mainConainer: { marginTop: "0px" },
}));

export interface IForm extends IAddBannedItem {}

interface Props {
  // In TS we make an interface of Props to shape them.
  handleSubmit: (formData: IForm) => Promise<void>;
  updateSubmit: (formData: IForm) => Promise<void>;
  initialValues?: IVehicleType; // '?' Its means initialValues is Optional
  loading?: any;

  tableRecord?: any;
  temp?: any;
}

export function InputFields({
  initialValues,
  handleSubmit,
  updateSubmit,
  loading,
  tableRecord,
}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [isEditMode, setisEditMode] = React.useState(false);
  const [submitCounter, setSubmitCounter] = useState<any>(0);
  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [allUnits, setAllUnits] = useState<any>([]);

  const validationSchema = object({
    name: string().required("This field is required").max(50, "Maximum 50 characters are allowed."),
    unitId: number().required("This field is required").min(1, "This field is required"),
  });
  const formik = useFormik<IForm>({
    enableReinitialize: true,
    initialValues: {
      id: initialValues?.id || 0,
      name: initialValues?.name || "",
      unitId: initialValues?.unitId || 0,
      isActive: initialValues?.isActive || Boolean(false),
      isBannedItem: 1,
    },
    validationSchema: validationSchema,
    onSubmit: (values, actions) => {
      if (isEditMode === false && isDuplicatedRecordInTable(tableRecord, values.name) !== -1) {
        dispatch(
          addNotificationAction({
            message: "Record already exist.",
            options: { variant: "error" },
          })
        );
        return;
      }
      if (isEditMode) updateSubmit(values);
      else {
        formik.setFieldValue("id", 0);
        handleSubmit(values);
      }
      actions.setSubmitting(false);
      actions.resetForm();
      setisEditMode(false);
      setIsDirtyValue(false);
      setSelectedRecord(null);
    },
  });
  const handleUpdateClick = (params: any) => {
    setSelectedRecord(params);
    formik.setFieldValue("name", params.name);
    formik.setFieldValue("unitId", params.unitId);
    formik.setFieldValue("id", params.id);
    formik.setFieldValue("isActive", params.isActive);
    setisEditMode(true);
  };
  const isDuplicatedRecordInTable = (array: any, value: any) => {
    return array.findIndex((i: any) => i.name === value);
  };
  useEffect(() => {
    if (submitCounter) formik.submitForm();
  }, [submitCounter]);

  useEffect(() => {
    getAllUnits();
  }, []);

  const getAllUnits = async () => {
    const res = await dispatch(getAllCargoUnits());
    setAllUnits(res.payload || []);
  };

  /* GRID Code */
  useEffect(() => {
    if (tableRecord.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "unit"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return `${row.original.unit == "Cubic meter" ? "m³" : row.original.unit} `;
              },
            }
          : column.accessorKey === "actions"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <NewCommonButton
                      onClick={() => {
                        setIsDirtyValue(true);
                        handleUpdateClick(row.original);
                      }}
                      icon={<EditIcon />}
                      isEdit={true}
                    >
                      Edit
                    </NewCommonButton>
                  </>
                );
              },
            }
          : column.accessorKey === "isActive"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isActive ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isActive ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [tableRecord]);

  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);
  function handleValueChange(e: any) {
    formik.handleChange(e);
    e.target.value ? setIsDirtyValue(true) : setIsDirtyValue(false);
  }

  function setIsDirtyValue(val: boolean) {
    setIsDirty(val);
    dispatch(setisDirtyValue(val));
  }

  return (
    <>
      {/* If prompt, when navigating to another page inside the app */}
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <form onSubmit={formik.handleSubmit}>
        <Backdrop className={classes.backdrop} open={loading}>
          <CommonLoader />
        </Backdrop>
        <Grid container direction="row" alignItems="flex-start">
          {/* First Section */}
          <>
            <Grid className={classes.UserMainPanel}>
              <Grid
                // Container
                container
                className={classes.mainConainer}
                direction="row"
                alignItems="center"
                spacing={2}
              >
                <Grid container item xs={12} lg={8} spacing={2}>
                  <Grid item xs={12} md={5} style={{ paddingLeft: "0px", paddingRight: 0 }}>
                    <Box>
                      <TextField
                        className="customTextField"
                        required
                        size="small"
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        label="Add Banned Item"
                        name="name"
                        placeholder="Add Banned Item"
                        disabled={false}
                        value={formik.values.name}
                        onChange={handleValueChange}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3} style={{ paddingLeft: "12px" }}>
                    <Box>
                      <CommonAutoCompleteField
                        cssClassName="customTextField"
                        required
                        error={formik.touched.unitId && Boolean(formik.errors.unitId)}
                        helperText={formik.touched.unitId && formik.errors.unitId}
                        label="UOM"
                        placeholder="UOM"
                        name="unitId"
                        disableClearable={true}
                        optionsType={allUnits}
                        getOptionLabel={(option: any) =>
                          option.unit == "Cubic meter" ? "m³" : option.unit || ""
                        }
                        onChange={(row: any) => {
                          formik.setFieldValue("unitId", row ? row.id : 0);
                        }}
                        value={allUnits.find(({ id }: any) => id === formik.values.unitId) || ""}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2} style={{ paddingLeft: "12px" }}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formik.values.isActive)}
                            onChange={formik.handleChange}
                            name="isActive"
                            color="primary"
                          />
                        }
                        label="Active"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2} style={{ paddingRight: 0 }}>
                    <Box mb={2} mt={1} style={{ float: "right" }}>
                      <CommonButton
                        isGreen
                        cssClass="customSubmitBtn"
                        disable={
                          selectedRecord
                            ? formik.values.name === selectedRecord.name &&
                              formik.values.unitId === selectedRecord.unitId &&
                              formik.values.isActive === selectedRecord.isActive
                            : JSON.stringify(formik.values) === JSON.stringify(formik.initialValues)
                        }
                        onClick={() => setSubmitCounter((prev: any) => prev + 1)}
                      >
                        {isEditMode ? "Update" : "Add"}
                      </CommonButton>
                    </Box>
                  </Grid>

                  <CommonMaterialGrid
                    defaultPerPageRecord={10}
                    height={400}
                    loader={false}
                    rows={tableRecord || []}
                    columns={displayColumns}
                  />
                </Grid>
                {/* End Inner Container */}
              </Grid>
              {/* End Outer Container */}
            </Grid>
          </>
        </Grid>
      </form>
    </>
  );
}
